/**
 * Station detail drawer: uses shared DetailDrawer + DetailDrawerContent.
 * Shows station info from API. Copyable fields, Lock/Unlock, comment, work time, connection block.
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import * as api from '../../modules/api';
import { searchPowerBankByStationParams } from '../../modules/powerbanks/powerbanksServices';
import SignalLevel from '../../utiles/Signals';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField, CopyableFieldWithAction } from '../components/DetailDrawerContent';
import EntityCommentsSection, { normalizeEntityComments } from '../components/EntityCommentsSection';
import {
  formatSimProviderLabel,
  hasSimInfo,
  pickNormalizedIccid,
} from '../utils/simDisplay';
import { formatApiDate } from '../utils/formatApiDate';
import { GUIDE_PHOTO_TYPES, asJpgFile, guidePhotoIndex } from '../constants/guidePhotoTypes';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function formatWorkTimeDay(dayValue, t) {
  if (!dayValue || !Array.isArray(dayValue)) return null;
  const a = dayValue[0];
  const b = dayValue[1];
  if (a === 'AllDay' || a === 'ANYTIME') return t('stationPage.allDay');
  if (a === 'NotWork') return t('stationPage.notWorking');
  if (a != null && b != null) return `${a} – ${b}`;
  return a != null ? String(a) : null;
}

function formatWorkTime(workTime, t) {
  if (!workTime || typeof workTime !== 'object') return [];
  const monday = workTime.monday;
  const legacyAnytime =
    Array.isArray(monday) &&
    (monday[0] === 'ANYTIME' || monday[0] === 'AllDay') &&
    DAY_KEYS.every((key) => {
      if (key === 'monday') return true;
      const arr = workTime[key];
      return !Array.isArray(arr) || arr.length === 0;
    });
  const source = legacyAnytime
    ? DAY_KEYS.reduce((acc, key) => ({ ...acc, [key]: ['AllDay'] }), {})
    : workTime;
  return DAY_KEYS.map((key) => {
    const label = key;
    const str = formatWorkTimeDay(source[key], t);
    return str != null ? { label, value: str } : null;
  }).filter(Boolean);
}

export default function StationDetailDrawer({ station, open, onClose, onRefresh, canManage = true }) {
  const { t } = useTranslation();
  const history = useHistory();
  const notifications = useNotifications();
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [powerbanksDialogOpen, setPowerbanksDialogOpen] = useState(false);
  const [powerbanksList, setPowerbanksList] = useState([]);
  const [powerbanksLoading, setPowerbanksLoading] = useState(false);
  const [powerbanksError, setPowerbanksError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reloadLoading, setReloadLoading] = useState(false);
  const [ejectingSlot, setEjectingSlot] = useState(null);
  const [ejectSlotInput, setEjectSlotInput] = useState('');
  const [ejectSlotLoading, setEjectSlotLoading] = useState(false);
  const [guideImages, setGuideImages] = useState([]);
  const [guideImagesLoading, setGuideImagesLoading] = useState(false);
  const [uploadPhotoDialogOpen, setUploadPhotoDialogOpen] = useState(false);
  const [uploadPhotoFile, setUploadPhotoFile] = useState(null);
  const [uploadPhotoType, setUploadPhotoType] = useState('streetView');
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);

  const handleEditNavigate = useCallback(() => {
    if (!canManage) return;
    const num = station?.stationNumber ?? station?.station?.stationNumber;
    if (num) {
      onClose();
      history.push(`/station/edit/${encodeURIComponent(num)}`);
    }
  }, [station?.stationNumber, station?.station?.stationNumber, onClose, history, canManage]);

  useEffect(() => {
    if (!open || !station) return;
    const entity = station.station && (station.comment == null && station.comments == null)
      ? { ...station.station, comment: station.station.comment, comments: station.station.comments }
      : station;
    setComments(normalizeEntityComments(entity));
  }, [open, station?.id, station?.comment, station?.comments, station?.station?.comment, station?.station?.comments]);

  const stationNumForApi = station?.stationNumber ?? station?.station?.stationNumber;
  useEffect(() => {
    if (!open || !stationNumForApi) return;
    setGuideImagesLoading(true);
    api.getStationAllImg(stationNumForApi)
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data) ? data : [];
        setGuideImages(list.map((item) => (typeof item === 'string' ? item : item?.url ?? item?.imageUrl ?? '')).filter(Boolean));
      })
      .catch(() => setGuideImages([]))
      .finally(() => setGuideImagesLoading(false));
  }, [open, stationNumForApi]);

  const handleCopy = useCallback(
    (text) => {
      if (!text || text === '—') return;
      navigator.clipboard.writeText(String(text)).then(
        () => notifications.show(t('drawer.copied'), { severity: 'success', autoHideDuration: 2000 }),
        () => notifications.show(t('drawer.copyFailed'), { severity: 'error' }),
      );
    },
    [notifications, t],
  );

  const handleLockUnlock = useCallback(async () => {
    const stationNumber = station?.stationNumber ?? station?.station?.stationNumber;
    if (!stationNumber) {
      notifications.show(t('drawer.station.stationNumberMissing'), { severity: 'error' });
      return;
    }
    const newLocked = !(station?.locked ?? station?.station?.locked);
    setLockLoading(true);
    try {
      await api.lockStation(stationNumber, newLocked);
      setLockDialogOpen(false);
      onRefresh?.();
      notifications.show(newLocked ? t('drawer.station.stationLocked') : t('drawer.station.stationUnlocked'), {
        severity: 'success',
        autoHideDuration: 3000,
      });
      onClose();
    } catch (e) {
      notifications.show(e?.message || t('drawer.actionFailed'), { severity: 'error' });
    } finally {
      setLockLoading(false);
    }
  }, [station?.stationNumber, station?.station?.stationNumber, station?.locked, station?.station?.locked, onRefresh, onClose, notifications]);

  const handleAddComment = useCallback(async (text) => {
    const sid = station?.id ?? station?.station?.id;
    if (!sid) return;
    const res = await api.commentStation(sid, text);
    const list = res?.data?.comments;
    if (Array.isArray(list)) setComments(list);
    else setComments((prev) => [...prev, { at: new Date().toISOString(), by: null, text }]);
    notifications.show(t('drawer.comments.added'), { severity: 'success', autoHideDuration: 2000 });
  }, [station?.id, station?.station?.id, notifications, t]);

  const loadGuideImages = useCallback(() => {
    const num = station?.stationNumber ?? station?.station?.stationNumber;
    if (!num) return;
    setGuideImagesLoading(true);
    api.getStationAllImg(num)
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data) ? data : [];
        // Prefer typed API objects; fall back to URL strings.
        const normalized = list.map((item, idx) => {
          if (typeof item === 'string') {
            return { type: GUIDE_PHOTO_TYPES[idx] || null, url: item };
          }
          return {
            type: item?.type || GUIDE_PHOTO_TYPES[idx] || null,
            url: item?.url ?? item?.imageUrl ?? '',
          };
        }).filter((p) => p.url);
        setGuideImages(normalized);
      })
      .catch(() => setGuideImages([]))
      .finally(() => setGuideImagesLoading(false));
  }, [station?.stationNumber, station?.station?.stationNumber]);

  const handleUploadPhotoOpen = useCallback(() => {
    setUploadPhotoFile(null);
    setUploadPhotoType('streetView');
    setUploadPhotoDialogOpen(true);
  }, []);

  const handleUploadPhotoSubmit = useCallback(async () => {
    const num = station?.stationNumber ?? station?.station?.stationNumber;
    if (!num || !uploadPhotoFile) {
      notifications.show(t('drawer.station.selectPhoto'), { severity: 'warning' });
      return;
    }
    setUploadPhotoLoading(true);
    try {
      const idx = guidePhotoIndex(uploadPhotoType) || 1;
      const file = asJpgFile(uploadPhotoFile, `${num}_${idx}.jpg`);
      await api.AddImage([file], num, [uploadPhotoType]);
      notifications.show(t('drawer.station.photoUploaded'), { severity: 'success', autoHideDuration: 2000 });
      setUploadPhotoDialogOpen(false);
      setUploadPhotoFile(null);
      setUploadPhotoType('streetView');
      loadGuideImages();
    } catch (e) {
      notifications.show(e?.response?.data?.message || e?.message || t('drawer.station.uploadFailed'), { severity: 'error' });
    } finally {
      setUploadPhotoLoading(false);
    }
  }, [station?.stationNumber, station?.station?.stationNumber, uploadPhotoFile, uploadPhotoType, loadGuideImages, notifications, t]);

  const loadPowerbanks = useCallback(() => {
    const num = station?.stationNumber ?? station?.station?.stationNumber;
    if (!num) return;
    setPowerbanksLoading(true);
    setPowerbanksError(null);
    searchPowerBankByStationParams({ value: num })
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data) ? data : data != null ? [data] : [];
        // Default API now returns only banks currently in slots (IN_STATION + FOREIGN_IN_STATION).
        // Keep a defensive filter in case of older backends / cached responses.
        setPowerbanksList(list.filter((pb) =>
          pb?.status === 'IN_STATION' || pb?.status === 'FOREIGN_IN_STATION'
        ));
      })
      .catch((e) => {
        setPowerbanksError(e?.message || t('drawer.station.failedToLoadPowerbanks'));
        setPowerbanksList([]);
      })
      .finally(() => setPowerbanksLoading(false));
  }, [station?.stationNumber, station?.station?.stationNumber, t]);

  const handlePowerbanksOpen = useCallback(() => {
    setPowerbanksDialogOpen(true);
    setPowerbanksList([]);
    setPowerbanksError(null);
    loadPowerbanks();
  }, [loadPowerbanks]);

  const handleEjectPowerbank = useCallback(async (slot) => {
    const sid = station?.stationId ?? station?.stationID ?? station?.station?.stationId ?? station?.station?.stationID;
    if (sid == null || slot == null) {
      notifications.show(t('drawer.station.stationOrSlotMissing'), { severity: 'error' });
      return;
    }
    setEjectingSlot(slot);
    try {
      await api.TakePowerBank(sid, slot);
      // Optimistic remove: extract keeps lastStation, so unfiltered API would still return the PB.
      setPowerbanksList((prev) => prev.filter((pb) => String(pb?.slot) !== String(slot)));
      notifications.show(t('drawer.station.powerbankEjected'), { severity: 'success', autoHideDuration: 3000 });
      loadPowerbanks();
      onRefresh?.();
    } catch (e) {
      notifications.show(e?.message || t('drawer.station.failedToEject'), { severity: 'error' });
    } finally {
      setEjectingSlot(null);
    }
  }, [station?.stationId, station?.stationID, station?.station?.stationId, station?.station?.stationID, loadPowerbanks, onRefresh, notifications, t]);

  const handleEjectBySlot = useCallback(async () => {
    const slot = parseInt(ejectSlotInput, 10);
    if (Number.isNaN(slot) || slot < 0) {
      notifications.show(t('drawer.station.enterValidSlot'), { severity: 'warning' });
      return;
    }
    const sid = station?.stationId ?? station?.stationID ?? station?.station?.stationId ?? station?.station?.stationID;
    if (!sid) {
      notifications.show(t('drawer.station.stationIdMissing'), { severity: 'error' });
      return;
    }
    setEjectSlotLoading(true);
    try {
      await api.TakePowerBank(sid, slot);
      setPowerbanksList((prev) => prev.filter((pb) => String(pb?.slot) !== String(slot)));
      notifications.show(t('drawer.station.powerbankEjected'), { severity: 'success', autoHideDuration: 3000 });
      setEjectSlotInput('');
      loadPowerbanks();
      onRefresh?.();
    } catch (e) {
      notifications.show(e?.message || t('drawer.station.failedToEject'), { severity: 'error' });
    } finally {
      setEjectSlotLoading(false);
    }
  }, [station, ejectSlotInput, loadPowerbanks, onRefresh, notifications, t]);

  const handleDelete = useCallback(async () => {
    const num = station?.stationNumber ?? station?.station?.stationNumber;
    if (!num) return;
    setDeleteLoading(true);
    try {
      await api.DeleteStation(num);
      setDeleteDialogOpen(false);
      notifications.show(t('drawer.station.stationDeleted'), { severity: 'success', autoHideDuration: 3000 });
      onRefresh?.();
      onClose();
    } catch (e) {
      notifications.show(e?.message || t('drawer.station.failedToDeleteStation'), { severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  }, [station?.stationNumber, station?.station?.stationNumber, onRefresh, onClose, notifications, t]);

  const handleReload = useCallback(async () => {
    const sid = station?.stationId ?? station?.stationID ?? station?.station?.stationId ?? station?.station?.stationID;
    if (!sid) {
      notifications.show(t('drawer.station.stationIdMissing'), { severity: 'error' });
      return;
    }
    setReloadLoading(true);
    try {
      await api.ReloadStation(sid);
      notifications.show(t('drawer.station.stationReloadSent'), { severity: 'success', autoHideDuration: 3000 });
      onRefresh?.();
    } catch (e) {
      notifications.show(e?.message || t('drawer.station.failedToReload'), { severity: 'error' });
    } finally {
      setReloadLoading(false);
    }
  }, [station?.stationId, station?.stationID, station?.station?.stationId, station?.station?.stationID, onRefresh, notifications, t]);

  const stationId = station?.stationId ?? station?.stationID ?? station?.station?.stationId ?? station?.station?.stationID;
  const stationNumber = station?.stationNumber ?? station?.station?.stationNumber;
  const address = station?.address ?? station?.station?.address;
  const additionalAddress = station?.additionalAddress ?? station?.station?.additionalAddress;
  const workTimeRaw = station?.workTime ?? station?.station?.workTime;
  const workTimeLines = formatWorkTime(workTimeRaw, t);
  const tariffName = station?.tariffPlan?.name ?? station?.station?.tariffPlan?.name;
  const locked = station?.locked ?? station?.station?.locked;
  const status = station?.status ?? station?.station?.status;
  const statusAt = station?.statusAt ?? station?.station?.statusAt;
  const latitude = station?.latitude ?? station?.station?.latitude;
  const longitude = station?.longitude ?? station?.station?.longitude;
  const numberSlots = station?.numberSlots ?? station?.station?.numberSlots;
  const owner = station?.owner ?? station?.station?.owner;
  const merchant = station?.merchant ?? station?.station?.merchant;
  const connectionMode = station?.connectionMode ?? station?.station?.connectionMode;
  const signalLevelVal = station?.signalLevel ?? station?.station?.signalLevel;
  const simNormalizedId = pickNormalizedIccid(station);
  const simProviderLabel = formatSimProviderLabel(station, t);
  const statusNormalized = status != null ? String(status).toUpperCase() : null;
  const isLocked = Boolean(locked);
  const isConnected = statusNormalized === 'CONNECTED';
  const connectionStatusDisplay = statusNormalized == null ? null : isConnected ? 'connected' : 'disconnected';
  const businessStatusDisplay = isLocked ? 'blocked' : 'active';

  const stationTypeLabel = numberSlots != null && numberSlots !== '' ? t('drawer.station.slotsCount', { count: numberSlots }) : '—';
  const ownerOrMerchant = owner ?? merchant ?? '—';
  const hasCoords = (latitude != null && latitude !== '') || (longitude != null && longitude !== '');
  const hasConnectionBlock =
    (connectionMode != null && connectionMode !== '') ||
    (signalLevelVal != null && signalLevelVal !== '') ||
    hasSimInfo(station);
  const dayLabelKey = { monday: 'dayMon', tuesday: 'dayTue', wednesday: 'dayWed', thursday: 'dayThu', friday: 'dayFri', saturday: 'daySat', sunday: 'daySun' };

  const handleOpenTariffs = () => {
    try {
      sessionStorage.setItem('crossLinkTariffName', String(tariffName || ''));
    } catch (_) {}
    window.location.hash = '#/tariff';
  };

  return (
    <>
      <DetailDrawer title={t('drawer.station.title')} open={open} onClose={onClose}>
        {!station ? (
          <Typography color="text.secondary">{t('drawer.station.noSelection')}</Typography>
        ) : (
        <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
          {/* Блок 1: Основные данные */}
          <Section title={t('drawer.station.mainInfo')}>
            <CopyableField label={t('drawer.station.stationId')} value={stationId} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
            <CopyableField label={t('drawer.station.stationNumber')} value={stationNumber} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
            <CopyableField label={t('drawer.station.address')} value={address} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
            <CopyableField label={t('drawer.station.installationPoint')} value={additionalAddress} onCopy={handleCopy} copyTooltip={t('drawer.copy')} />
            {hasCoords && (
              <Box sx={{ py: 0.75, minWidth: 0, maxWidth: '100%' }}>
                <Typography variant="caption" color="text.secondary" display="block">{t('drawer.station.coordinates')}</Typography>
                <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
                  {([latitude, longitude].filter(Boolean).join(', ')) ? (
                    <Tooltip title={t('drawer.copy')}>
                      <Button
                        variant="text"
                        color="primary"
                        size="small"
                        onClick={() => handleCopy([latitude, longitude].filter(Boolean).join(', '))}
                        aria-label={t('drawer.copy')}
                        sx={{
                          textTransform: 'none',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          minWidth: 0,
                          flex: '1 1 auto',
                          justifyContent: 'flex-start',
                          py: 0,
                          px: 0,
                          minHeight: 'auto',
                          lineHeight: 1.43,
                          textAlign: 'left',
                        }}
                      >
                        {[latitude, longitude].filter(Boolean).join(', ') || '—'}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', flex: '1 1 auto', minWidth: 0 }}>—</Typography>
                  )}
                  {latitude != null && latitude !== '' && longitude != null && longitude !== '' && (
                    <Tooltip title={t('drawer.station.showOnMap')}>
                      <IconButton
                        size="small"
                        aria-label={t('drawer.station.showOnMap')}
                        component="a"
                        href={`https://yandex.ru/maps/?pt=${longitude},${latitude}&z=17`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ flexShrink: 0 }}
                      >
                        <MapOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            )}
            <Field label={t('drawer.station.stationType')} value={stationTypeLabel} />
            <Field label={t('drawer.station.ownerMerchant')} value={ownerOrMerchant} />
            <CopyableFieldWithAction
              label={t('drawer.station.tariff')}
              value={tariffName}
              onCopy={handleCopy}
              onOpen={tariffName ? handleOpenTariffs : undefined}
              copyTooltip={t('drawer.copy')}
              openTooltip={t('drawer.station.openInTariffs')}
            />
          </Section>

          {/* Блок 4: Связь и SIM */}
          {hasConnectionBlock && (
            <Section title={t('drawer.station.connectionSim')}>
              {connectionMode != null && connectionMode !== '' && (
                <Box sx={{ py: 0.75, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t('drawer.station.connectionMode')}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{connectionMode}</Typography>
                </Box>
              )}
              {signalLevelVal != null && signalLevelVal !== '' && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: 0.75, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{t('drawer.station.signalLevel')}</Typography>
                  <Box sx={{ flexShrink: 0, lineHeight: 0, '& img': { width: 24, height: 24, display: 'block' } }}>
                    <SignalLevel signal={signalLevelVal} />
                  </Box>
                </Stack>
              )}
              {hasSimInfo(station) && (
                <Box sx={{ py: 0.75, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t('drawer.station.simProvider')}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{simProviderLabel}</Typography>
                </Box>
              )}
              {simNormalizedId ? (
                <CopyableField
                  label={t('drawer.station.simCardId')}
                  value={simNormalizedId}
                  onCopy={handleCopy}
                  copyTooltip={t('drawer.copy')}
                />
              ) : null}
            </Section>
          )}

          {/* Блок 5: Статус и блокировка */}
          <Section title={t('drawer.station.statusLock')}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: 0.75 }}>
              <Typography variant="body2" color="text.secondary">{t('drawer.station.connectionStatus')}</Typography>
              {connectionStatusDisplay ? (
                <Chip
                  label={connectionStatusDisplay === 'connected' ? t('stations.connected') : t('stations.disconnected')}
                  color={connectionStatusDisplay === 'connected' ? 'success' : 'error'}
                  size="small"
                />
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: 0.75 }}>
              <Typography variant="body2" color="text.secondary">{t('drawer.station.businessStatus')}</Typography>
              <Chip
                label={businessStatusDisplay === 'blocked' ? t('drawer.station.blocked') : t('drawer.station.active')}
                color={businessStatusDisplay === 'blocked' ? 'error' : 'success'}
                size="small"
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: 0.75 }}>
              <Typography variant="body2" color="text.secondary">{t('drawer.station.statusUpdated')}</Typography>
              <Typography variant="body2">{formatApiDate(statusAt)}</Typography>
            </Stack>
            <Button
              variant="outlined"
              color={locked ? 'success' : 'error'}
              startIcon={locked ? <LockOpenIcon /> : <LockIcon />}
              onClick={() => setLockDialogOpen(true)}
              fullWidth
              sx={{ mt: 1 }}
              disabled={!canManage}
            >
              {locked ? t('drawer.station.unlockStation') : t('drawer.station.blockStation')}
            </Button>
          </Section>

          {/* Режим работы — ниже статуса */}
          {workTimeLines.length > 0 && (
            <Section title={t('drawer.station.workHours')}>
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                {workTimeLines.map(({ label, value }) => (
                  <Stack key={label} direction="row" justifyContent="space-between" gap={1} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {t(`drawer.station.${dayLabelKey[label] || label}`)}
                    </Typography>
                    <Typography variant="body2" sx={{ minWidth: 0, textAlign: 'right', wordBreak: 'break-word' }}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Section>
          )}

          {/* Как найти — ниже статуса */}
          <Section title={t('drawer.station.howToFind')}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('drawer.station.guidePhotosDescription')}
            </Typography>
            {guideImagesLoading ? (
              <Typography variant="body2" color="text.secondary">{t('drawer.station.loading')}</Typography>
            ) : guideImages.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                {guideImages.map((photo, idx) => (
                  <Box key={photo.url || idx} sx={{ width: 80 }}>
                    <Box
                      component="img"
                      src={photo.url}
                      alt={photo.type || ''}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'block',
                      }}
                    />
                    {photo.type ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                        {t(`stationPage.guidePhotoType_${photo.type}`, { defaultValue: photo.type })}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('drawer.station.noPhotosYet')}</Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
              onClick={handleUploadPhotoOpen}
              disabled={!canManage}
            >
              {t('drawer.station.uploadPhoto')}
            </Button>
          </Section>

          {/* Действия */}
          <Section title={t('drawer.station.actions')}>
            {canManage && (
              <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={handleEditNavigate} fullWidth sx={{ mb: 0.5 }}>
                {t('drawer.station.edit')}
              </Button>
            )}
            <Button variant="outlined" startIcon={<BatteryChargingFullRoundedIcon />} onClick={handlePowerbanksOpen} fullWidth sx={{ mb: 0.5 }}>
              {t('drawer.station.powerbanks')}
            </Button>
            {canManage && (
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReload} disabled={reloadLoading} fullWidth sx={{ mb: 0.5 }}>
                {reloadLoading ? t('drawer.station.reloading') : t('drawer.station.reloadStation')}
              </Button>
            )}
            {canManage && (
              <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteDialogOpen(true)} fullWidth>
                {t('drawer.station.deleteStation')}
              </Button>
            )}
          </Section>

          {/* Блок 7: Комментарии */}
          <EntityCommentsSection
            comments={comments}
            canAdd={canManage}
            onAdd={handleAddComment}
          />
        </Stack>
        )}
      </DetailDrawer>

      <Dialog
        open={lockDialogOpen}
        onClose={() => !lockLoading && setLockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{locked ? t('drawer.station.unlockStation') : t('drawer.station.lockStation')}</DialogTitle>
        <DialogContent>
          <Typography>
            {locked
              ? t('drawer.station.unlockStationConfirm', { number: stationNumber })
              : t('drawer.station.lockStationConfirm', { number: stationNumber })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)} disabled={lockLoading}>
            {t('drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color={locked ? 'success' : 'error'}
            onClick={handleLockUnlock}
            disabled={lockLoading}
          >
            {lockLoading ? '…' : locked ? t('drawer.station.unlockStation') : t('drawer.station.lockStation')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadPhotoDialogOpen}
        onClose={() => !uploadPhotoLoading && setUploadPhotoDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('drawer.station.uploadGuidePhoto')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="guide-photo-type-label">{t('drawer.station.photoType')}</InputLabel>
              <Select
                labelId="guide-photo-type-label"
                label={t('drawer.station.photoType')}
                value={uploadPhotoType}
                onChange={(e) => setUploadPhotoType(e.target.value)}
              >
                {GUIDE_PHOTO_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`stationPage.guidePhotoType_${type}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {t(`stationPage.guidePhotoHint_${uploadPhotoType}`)}
            </Typography>
            <input
              accept="image/*"
              type="file"
              id="guide-photo-file"
              style={{ display: 'none' }}
              onChange={(e) => setUploadPhotoFile(e.target.files?.[0] || null)}
            />
            <Button
              variant="outlined"
              component="label"
              htmlFor="guide-photo-file"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
            >
              {uploadPhotoFile ? uploadPhotoFile.name : t('drawer.station.choosePhoto')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadPhotoDialogOpen(false)} disabled={uploadPhotoLoading}>
            {t('drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadPhotoSubmit}
            disabled={uploadPhotoLoading || !uploadPhotoFile}
          >
            {uploadPhotoLoading ? t('drawer.station.uploading') : t('drawer.station.uploadPhoto')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={powerbanksDialogOpen} onClose={() => setPowerbanksDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('drawer.station.powerbanksAtStation')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">{t('drawer.station.ejectFromSlot')}</Typography>
              <TextField
                type="number"
                size="small"
                placeholder={t('drawer.station.slot')}
                value={ejectSlotInput}
                onChange={(e) => setEjectSlotInput(e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ width: 80 }}
              />
              <Button size="small" variant="outlined" startIcon={<ExitToAppOutlinedIcon />} onClick={handleEjectBySlot} disabled={ejectSlotLoading}>
                {ejectSlotLoading ? '…' : t('drawer.station.eject')}
              </Button>
            </Stack>
            {powerbanksError && (
              <Typography variant="body2" color="error">{powerbanksError}</Typography>
            )}
            {powerbanksLoading ? (
              <Typography variant="body2" color="text.secondary">{t('drawer.station.loading')}</Typography>
            ) : powerbanksList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('drawer.station.noPowerbanksAtStation')}</Typography>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('drawer.station.battery')}</TableCell>
                    <TableCell>{t('drawer.station.serialNumber')}</TableCell>
                    <TableCell>{t('drawer.station.slot')}</TableCell>
                    <TableCell align="right">{t('drawer.station.eject')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {powerbanksList.map((pb, index) => {
                    const battery = pb.batteryLevelPercent != null && pb.batteryLevelPercent >= 0 && pb.batteryLevelPercent <= 100
                      ? `${pb.batteryLevelPercent}%`
                      : '—';
                    const serial = pb.powerBankId ?? pb.id ?? '—';
                    const slot = pb.slot ?? '—';
                    const isEjecting = ejectingSlot === slot;
                    return (
                      <TableRow key={pb.id || pb.powerBankId || `row-${index}`}>
                        <TableCell>{battery}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{serial}</TableCell>
                        <TableCell>{slot}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ExitToAppOutlinedIcon />}
                            onClick={() => handleEjectPowerbank(slot)}
                            disabled={isEjecting || slot === '—'}
                          >
                            {isEjecting ? '…' : t('drawer.station.eject')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPowerbanksDialogOpen(false)}>{t('drawer.close')}</Button>
          <Button variant="outlined" onClick={loadPowerbanks} disabled={powerbanksLoading}>{t('drawer.refresh')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('drawer.station.deleteStationTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('drawer.station.deleteStationConfirm', { number: stationNumber })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>{t('drawer.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? '…' : t('drawer.station.deleteStation')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
